import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle2, Mail, Sparkles, Camera, AlertTriangle, ArrowRight } from 'lucide-react';
import { clearRoleSwitchContext, getRoleLandingPath, getRoleSwitchContext } from '../../utils/roleSwitchFlow';
import { clearPendingPartnerIntent } from '../../utils/partnerIntent';

const FONT = "'Inria Serif', serif";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// A few floating sparkle dots that drift behind the success badge —
// purely decorative, gives the screen a celebratory finish without
// being noisy.
const SPARKS = [
  { left: '18%', top: '14%', size: 6, delay: 0.4 },
  { left: '78%', top: '10%', size: 8, delay: 0.6 },
  { left: '12%', top: '36%', size: 5, delay: 0.9 },
  { left: '82%', top: '40%', size: 7, delay: 1.1 },
  { left: '24%', top: '58%', size: 5, delay: 1.4 },
  { left: '70%', top: '62%', size: 6, delay: 1.6 },
];

export default function VenueOnboardingComplete() {
  const navigate = useNavigate();
  const [venueName, setVenueName] = useState('');
  const [mediaChangeNotice, setMediaChangeNotice] = useState(null);

  useEffect(() => {
    // Landing here means the venue application was submitted or the
    // owner completed the initial media step — the fresh-signup partner
    // intent has served its purpose. Wipe it so a later cold-launch
    // stops re-routing back to /venue/onboarding/1.
    clearPendingPartnerIntent();
    try {
      const draft = JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}');
      setVenueName(draft.restaurantName || draft.invitedVenueName || '');
    } catch {
      /* ignore */
    }
  }, []);

  // Pull any "media change requested" notification so we can surface an
  // inline action banner instead of forcing the owner to dig into the bell.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/notifications/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = Array.isArray(res.data) ? res.data : [];
        const latest = items.find(
          (item) => item?.type === 'venue_media_change_requested' && !item?.is_read,
        );
        if (latest && !cancelled) setMediaChangeNotice(latest);
      } catch {
        /* notification fetch is best-effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleResumeMediaUpload = async () => {
    if (!mediaChangeNotice) {
      navigate('/venue/intro');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/notifications/${mediaChangeNotice.id}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      /* ignore — navigation still proceeds */
    }
    sessionStorage.removeItem('venueOnboarding');
    navigate('/venue/intro');
  };

  const handleDone = () => {
    const onboardingState = JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}');
    const isAddingVenue = onboardingState.isAddingVenue;
    const isRoleSwitch = onboardingState.isRoleSwitch;
    const returnPath = onboardingState.returnPath || '/venue/analytics';
    const returnVenueAppId = onboardingState.returnVenueAppId;
    sessionStorage.removeItem('venueOnboarding');
    if (isAddingVenue || isRoleSwitch) {
      if (returnVenueAppId) {
        localStorage.setItem('activeVenueAppId', returnVenueAppId);
      }
      clearRoleSwitchContext();
      navigate(returnPath, { replace: true });
    } else {
      const roleSwitchContext = getRoleSwitchContext();
      if (roleSwitchContext?.returnPath) {
        clearRoleSwitchContext();
        navigate(roleSwitchContext.returnPath, { replace: true });
      } else {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        window.location.href = getRoleLandingPath(user.role || 'guest');
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="app-shell"
      data-testid="venue-complete-page"
      style={{
        minHeight: '100dvh',
        background: 'var(--app-bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 'env(safe-area-inset-bottom, 0)',
        overflow: 'hidden',
      }}
    >
      {/* Floating decorative sparkles */}
      {SPARKS.map((s, i) => (
        <motion.span
          key={i}
          aria-hidden="true"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: [0, 0.9, 0.4, 0.9], scale: [0.6, 1, 0.85, 1], y: [0, -6, 2, -3] }}
          transition={{
            duration: 4.2,
            delay: s.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          style={{
            position: 'absolute',
            left: s.left,
            top: s.top,
            width: s.size,
            height: s.size,
            borderRadius: '50%',
            background:
              'radial-gradient(circle at 30% 30%, #FFFFFF 0%, rgba(232, 25, 44, 0.35) 55%, var(--accent-purple) 100%)',
            boxShadow: '0 0 12px rgba(232, 25, 44, 0.45)',
            pointerEvents: 'none',
          }}
        />
      ))}

      {/* Inline action banner — admin requested specific photos/video to be re-uploaded */}
      {mediaChangeNotice ? (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          data-testid="venue-complete-media-change-banner"
          style={{
            width: '100%',
            maxWidth: 460,
            marginTop: 76,
            padding: '14px 16px',
            borderRadius: 16,
            background: 'var(--status-warning-surface)',
            border: '1px solid rgba(214, 137, 16, 0.25)',
            boxShadow: '0 12px 28px rgba(214, 137, 16, 0.18)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            zIndex: 3,
          }}
        >
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 11,
              background: 'rgba(214, 137, 16, 0.14)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AlertTriangle size={18} color="#B56B11" strokeWidth={2.2} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontFamily: FONT, fontSize: 15, fontWeight: 700, color: 'var(--status-warning-text)', letterSpacing: '-0.005em' }}>
              {(mediaChangeNotice.metadata?.field_issues?.length || 0) > 0
                ? 'Allure asked you to revise your venue'
                : 'Allure asked you to refresh some media'}
            </p>
            <p
              data-testid="venue-complete-media-change-banner-note"
              style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--status-warning-text)', lineHeight: 1.45, fontWeight: 500, opacity: 0.85 }}
            >
              {mediaChangeNotice.metadata?.admin_note
                ? `“${mediaChangeNotice.metadata.admin_note}”`
                : 'Open your venue onboarding to apply the requested fixes and resubmit.'}
            </p>
            {(mediaChangeNotice.metadata?.field_issues?.length || 0) > 0 ? (
              <ul
                data-testid="venue-complete-media-change-banner-issues"
                style={{ margin: '8px 0 0 18px', padding: 0, fontSize: 12, color: 'var(--status-warning-text)', lineHeight: 1.55, fontWeight: 500, opacity: 0.85 }}
              >
                {(mediaChangeNotice.metadata.field_issues || []).map((issue, idx) => {
                  const labels = {
                    contact: 'Contact details',
                    business: 'Business / VAT',
                    address: 'Address & location',
                    hours: 'Operating hours',
                    social: 'Social media',
                    type: 'Venue type & categories',
                    other: 'Other details',
                  };
                  const label = labels[issue?.category] || (issue?.category || 'Details');
                  return (
                    <li key={`issue-${idx}`}>
                      <strong style={{ fontWeight: 700, color: 'var(--status-warning-text)' }}>{label}</strong>
                      {issue?.note ? ` — ${issue.note}` : ''}
                    </li>
                  );
                })}
              </ul>
            ) : null}
            <button
              type="button"
              onClick={handleResumeMediaUpload}
              data-testid="venue-complete-media-change-banner-cta"
              style={{
                marginTop: 12,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '9px 14px',
                borderRadius: 12,
                border: 'none',
                background: 'linear-gradient(135deg, #F39A24, #D6890F)',
                color: '#FFFFFF',
                fontFamily: FONT,
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 10px 22px rgba(214, 137, 16, 0.32)',
              }}
            >
              Open onboarding to fix
              <ArrowRight size={14} strokeWidth={2.4} />
            </button>
          </div>
        </motion.div>
      ) : null}

      {/* Success badge — central hero */}
      <motion.div
        initial={{ scale: 0.4, opacity: 0, rotate: -8 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        transition={{ duration: 0.55, delay: 0.15, ease: [0.22, 1.4, 0.36, 1] }}
        style={{
          marginTop: mediaChangeNotice ? 28 : 92,
          width: 116,
          height: 116,
          borderRadius: '50%',
          background: 'var(--accent-purple)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow:
            'var(--premium-shadow-main)',
          position: 'relative',
        }}
      >
        <motion.span
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.4, ease: [0.22, 1.4, 0.36, 1] }}
        >
          <CheckCircle2 size={58} color="#FFFFFF" strokeWidth={2.2} />
        </motion.span>
        <motion.span
          initial={{ scale: 0.6, opacity: 0, rotate: -30 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ duration: 0.5, delay: 0.55, ease: [0.22, 1.6, 0.36, 1] }}
          style={{
            position: 'absolute',
            top: -6,
            right: -6,
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'var(--surface)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'var(--premium-shadow-main)',
          }}
        >
          <Sparkles size={16} color="var(--accent-purple)" strokeWidth={2.4} />
        </motion.span>
      </motion.div>

      {/* Heading + subheading */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
        style={{ textAlign: 'center', marginTop: 28, maxWidth: 380, zIndex: 2 }}
      >
        <h1
          data-testid="venue-complete-title"
          style={{
            margin: 0,
            fontFamily: FONT,
            fontSize: 30,
            lineHeight: 1.2,
            fontWeight: 700,
            color: 'var(--text)',
            letterSpacing: '-0.01em',
          }}
        >
          {venueName ? `Welcome, ${venueName}!` : 'You are all set!'}
        </h1>
        <p
          data-testid="venue-complete-subtitle"
          style={{
            margin: '12px 0 0',
            fontFamily: FONT,
            fontSize: 16,
            lineHeight: 1.55,
            color: 'var(--text-2)',
            fontWeight: 500,
          }}
        >
          You are in. Head into the app to finish your venue profile — once photos and details are ready, our team will review and get you live.
        </p>
      </motion.div>

      {/* Info cards — what happens next */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
        style={{
          marginTop: 28,
          width: '100%',
          maxWidth: 360,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          zIndex: 2,
        }}
      >
        <InfoRow
          icon={<Camera size={18} color="var(--accent-purple)" strokeWidth={2.2} />}
          title="Finish your venue profile"
          subtitle="Add photos, hours and the little details that make guests want to visit."
          testId="venue-complete-row-review"
          delay={0.7}
        />
        <InfoRow
          icon={<Mail size={18} color="var(--accent-purple)" strokeWidth={2.2} />}
          title="Then we review — you'll get an email"
          subtitle="Once your profile is complete, our team vets it and lets you know when you go live."
          testId="venue-complete-row-email"
          delay={0.82}
        />
      </motion.div>

      {/* Spacer pushes the CTA to the bottom */}
      <div style={{ flex: 1, minHeight: 28 }} />

      {/* Primary CTA */}
      <motion.button
        whileTap={{ scale: 0.97 }}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
        onClick={handleDone}
        data-testid="venue-complete-done"
        style={{
          width: '100%',
          maxWidth: 360,
          padding: '15px 20px',
          marginBottom: 24,
          borderRadius: 16,
          border: 'none',
          background: 'var(--accent-purple)',
          color: '#FFFFFF',
          fontFamily: FONT,
          fontSize: 16,
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: 'var(--premium-shadow-main)',
          zIndex: 2,
        }}
      >
        Done
      </motion.button>
    </motion.div>
  );
}

function InfoRow({ icon, title, subtitle, testId, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
      data-testid={testId}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 16px',
        borderRadius: 16,
        background: 'var(--surface)',
        border: '1px solid var(--card-border)',
        boxShadow: 'var(--premium-shadow-nested)',
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 11,
          background: 'var(--accent-surface-soft)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontFamily: FONT,
            fontSize: 15,
            fontWeight: 700,
            color: 'var(--text)',
            letterSpacing: '-0.005em',
          }}
        >
          {title}
        </p>
        <p
          style={{
            margin: '4px 0 0',
            fontFamily: FONT,
            fontSize: 13,
            color: 'var(--text-3)',
            lineHeight: 1.5,
            fontWeight: 500,
          }}
        >
          {subtitle}
        </p>
      </div>
    </motion.div>
  );
}
